import React, {useState, useEffect, useMemo, useImperativeHandle} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {vendorsActions} from '../../../../state/actions/vendors.actions';
import VendorSelect from '../VendorSelect';
import AddVendorModal from '../../VendorsPage/AddVendorModal';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {SCAActionsConstants} from '../../../../constants';
import {firebaseEvents} from '../../../../snippets/firebase';
import {scaHelpers, systemHelpers} from '../../../../utils/helpers';

const {VENDOR_CREATION_ACTION} = SCAActionsConstants;

const VendorSelectorWithAuth = React.forwardRef(({
  isAdmin,
  createVendor,
  getBeneficiary,
  getVendors,
  updateVendor,
  onBeneficiaryChange,
  onChange,
  onSubmitSuccess,
  reloadOnMissingValue = false,
  selectProps,
  showBeneficiaryInfo = true,
  value,
  vendorFormSize = 'short',
}, ref) => {
  const [addVendorModalProps, setAddVendorModalProps] = useState({
    loading: false,
    open: false,
    size: 'full'
  });
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [tempData, setTempData] = useState({
    data: undefined,
    errorCallbackFunction: undefined,
    successCallbackFunction: undefined,
  });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [vendors, setVendors] = useState({data: [], loading: false});

  const [t] = useTranslation(['main', 'vendors']);

  const placeholder = t('placeholderTemplates.select', {name: t('vendor').toLowerCase()});

  const addVendorModalMode = useMemo(() => {
    return addVendorModalProps.size === 'vat' ? 'edit' : 'add';
  }, [addVendorModalProps?.size]);

  const beneficiaryProps = useMemo(() => {
    const vendor = vendors.data.find(vendor => vendor.vendor_id === value);
    return {
      beneficiary: vendor?.beneficiary_id || null,
      vendor
    }
  }, [value, vendors?.data]);

  const formattedVendors = useMemo(() => ({
    data: vendors.data.map(vendor => {
      return {
        ...vendor,
        iban: vendor.iban || beneficiaries.find(beneficiary => beneficiary.vendor_id === vendor.vendor_id)?.clear_iban || ''
      }
    }),
    loading: vendors.loading
  }), [beneficiaries, vendors]);

  useEffect(() => {
    onBeneficiaryChange?.(beneficiaryProps);
  }, [beneficiaryProps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!reloadOnMissingValue || !value) return;

    const isVendorInList = vendors.data.some(vendor => vendor.vendor_id === value);
    if (isVendorInList) return;

    loadVendors(value);
  }, [reloadOnMissingValue, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVendors = (value) => {
    if (value) {
      loadVendorsListWithBeneficiary(value);
    } else if (!vendors.data.length) {
      loadVendorsList();
    }
  }

  useEffect(() => {
    loadVendors(value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    loadVendors, // expose the method
  }));

  const loadVendorsListWithBeneficiary = (value) => {
    loadVendorsList({
      successCallback: (vendors) => {
        if (showBeneficiaryInfo) {
          loadBeneficiaryDetails(value, vendors);
        }
      }
    });
  }

  const loadVendorsList = ({successCallback} = {}) => {
    if (vendors.data.length && !successCallback) return;

    getVendors({
      query: {per_page: 500},
      successCallback: (response) => {
        const updatedVendors = {...vendors, data: response.results, loading: false};
        setVendors(updatedVendors);
        successCallback?.(updatedVendors);
      },
      errorCallback: () => setVendors({...vendors, data: [], loading: false})
    })
  }

  const loadBeneficiaryDetails = (value, vendorsObj = vendors) => {
    if (!value) return;

    const beneficiaryId = vendorsObj.data.find(vendor => vendor.vendor_id === value)?.beneficiary_id;
    const beneficiaryObj = beneficiaries.find(beneficiary => beneficiary.id === beneficiaryId);
    if (!beneficiaryId || beneficiaryObj) return;

    getBeneficiary({
      id: beneficiaryId,
      successCallback: (beneficiary) => setBeneficiaries([...beneficiaries, beneficiary]),
    });
  }

  const handleOnChange = (updatedValue) => {
    if (updatedValue === value) return;
    onChange && onChange(updatedValue);

    if (showBeneficiaryInfo) {
      loadBeneficiaryDetails(updatedValue);
    }
  }

  const handleCreateVendor = ({ data, successCallback, errorCallback } = {}) => {
    const isEditMode = addVendorModalMode === 'edit';

    if (isEditMode && !value) return;

    setAddVendorModalProps({...addVendorModalProps, loading: true})

    const handleError = (response) => {
      scaHelpers.SCAResponseCallback({
        response,
        scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
        errorCallback: (response) => {
          errorCallback?.(response);
          setAddVendorModalProps({...addVendorModalProps, loading: false});
        }
      });
    };

    const handleSuccess = (response) => {
      const logEventName = isEditMode ? firebaseEvents.VENDORS_UPDATE : firebaseEvents.VENDORS_ADD_NEW;
      systemHelpers.logEvent(logEventName);

      handleCancelAddVendorModal();

      if (isEditMode) {
        onSubmitSuccess?.(response);
      } else {
        setVendors({...vendors, data: [response, ...vendors.data]});
        handleOnChange(response?.vendor_id);
      }

      successCallback?.(response);
    };

    const commonPayload = {
      data,
      headers: scaHelpers.getAuthHeaders(VENDOR_CREATION_ACTION),
      successCallback: handleSuccess,
      errorCallback: handleError
    };

    if (isEditMode) {
      updateVendor({ id: value, ...commonPayload });
    } else {
      createVendor(commonPayload);
    }
  };

  const handleCancelAddVendorModal = () => setAddVendorModalProps({...addVendorModalProps, loading: false, open: false});

  const handleOpenAddVendorModal = (e, size) => {
    setAddVendorModalProps({...addVendorModalProps, size: size || vendorFormSize, open: true});
  }

  const handleOkAddVendorModal = (data, successCallback, errorCallback) => {
    handleCreateVendor({data, successCallback, errorCallback});
    setTempData({
      ...tempData,
      data,
      errorCallbackFunction: errorCallback,
      successCallbackFunction: successCallback
    });
  }

  const handleCancelAuthModal = () => {
    addVendorModalProps.loading && setAddVendorModalProps({...addVendorModalProps, loading: false});
    setAuthWindowProps({...authWindowProps, open: false});
  }

  const handleOnSuccessAuthModal = () => {
    setAuthWindowProps({...authWindowProps, open: false});
    handleCreateVendor(tempData);
  }

  return (
    <>
      <VendorSelect
        placeholder={placeholder}
        {...selectProps}
        isEnableAdd={isAdmin}
        onAdd={handleOpenAddVendorModal}
        onChange={handleOnChange}
        value={value}
        vendors={formattedVendors}
      />

      <AddVendorModal
        {...addVendorModalProps}
        mode={addVendorModalMode}
        onCancel={handleCancelAddVendorModal}
        onOk={handleOkAddVendorModal}
        size={addVendorModalProps.size !== vendorFormSize ? addVendorModalProps.size : vendorFormSize}
      />

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={{title: `${t('add')} ${t('new')} ${t('vendor')}`}}
        handleCancel={handleCancelAuthModal}
        onSuccess={handleOnSuccessAuthModal}
        operationName={VENDOR_CREATION_ACTION}
      />
    </>
  );
});

VendorSelectorWithAuth.propTypes = {
  onBeneficiaryChange: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  reloadOnMissingValue: PropTypes.bool,
  selectProps: PropTypes.object,
  showBeneficiaryInfo: PropTypes.bool,
  value: PropTypes.string,
  vendorFormSize: PropTypes.oneOf(['banking', 'full', 'short'])
};

const mapStateToProps = ({ user: { isAdmin } }) => ({
  isAdmin,
});

const mapDispatchToProps = {
  createVendor: vendorsActions.createVendor,
  updateVendor: vendorsActions.updateVendor,
  getBeneficiary: vendorsActions.getBeneficiary,
  getVendors: vendorsActions.getVendors,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(VendorSelectorWithAuth);
